import {
  Directive,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BaseSiteService } from '@spartacus/core';
import { take } from 'rxjs';
import { AddressSuggestionsComponent } from './account-registration/address-suggestions/address-suggestions.component';
import { GoogleMapsLoaderService } from './google-maps-loader.service';

@Directive({
    selector: '[googleAddress]',
    exportAs: 'googleAddress',
    standalone: false
})
export class GoogleAddressDirective implements OnInit, OnDestroy {
  @Output() addressChosen = new EventEmitter<google.maps.places.PlaceResult>();

  private autocomplete?: google.maps.places.Autocomplete;
  private listener?: google.maps.MapsEventListener;
  private hasSelected = false;
  private placesService?: google.maps.places.PlacesService;
  private autocompleteService?: google.maps.places.AutocompleteService;
  private baseSiteId: string;
  private modalOpen: boolean = false;

  constructor(
    private el: ElementRef<HTMLInputElement>,
    private loader: GoogleMapsLoaderService,
    private baseSiteService: BaseSiteService,
    private readonly modalService: NgbModal
  ) {
    this.baseSiteService
      .getActive()
      .pipe(take(1))
      .subscribe((res) => {
        this.baseSiteId = res;
      });
  }

  async ngOnInit() {
    try {
      await this.loader.load();
      this.initAutocomplete();
      this.el.nativeElement.addEventListener('blur', () => this.onBlur());
      this.el.nativeElement.addEventListener('focus', () => this.onFocus());
    } catch (error) {
      console.error('Failed to load Google Maps API:', error);
    }
  }

  onFocus(): void {
    this.hasSelected = false;
  }

  private initAutocomplete() {
    this.autocompleteService = new google.maps.places.AutocompleteService();
    this.placesService = new google.maps.places.PlacesService(
      document.createElement('div')
    );

    this.autocomplete = new google.maps.places.Autocomplete(
      this.el.nativeElement,
      {
        types: ['address'],
        componentRestrictions: { country: 'us' },
        fields: [
          'ALL'
        ],
      }
    );

    this.listener = this.autocomplete.addListener('place_changed', async () => {
      this.hasSelected = true;
      const place = this.autocomplete!.getPlace();
      await this.addressChosen.emit(place);
    });
  }

  private async onBlur() {
    setTimeout(async () => {
      $(this.autocomplete).remove();
      if (this.hasSelected || this.modalOpen) return;

      const raw = this.el.nativeElement.value.trim();
      if (!raw) return;

      const predictions = await this.fetchPredictions(raw);
      if (!predictions.length) return;

      const suggestionPromises = predictions.map(
        (p) =>
          new Promise<{ placeId: string; display: string }>((resolve) =>
            this.placesService!.getDetails(
              {
                placeId: p.place_id,
                fields: ['address_components', 'formatted_address'],
              },
              (place) => {
                if (!place) {
                  return resolve({
                    placeId: p.place_id,
                    display: p.description,
                  });
                }

                const components = place.address_components || [];
                const streetNumber =
                  components.find((c) => c.types.includes('street_number'))
                    ?.long_name || '';
                const route =
                  components.find((c) => c.types.includes('route'))
                    ?.long_name || '';
                const subpremise =
                  components.find((c) => c.types.includes('subpremise'))
                    ?.long_name || '';
                const neighborhood =
                  components.find((c) => c.types.includes('neighborhood'))
                    ?.long_name || '';
                const sublocality =
                  components.find((c) => c.types.includes('sublocality'))
                    ?.long_name || '';
                const locality =
                  components.find((c) => c.types.includes('locality'))
                    ?.long_name || '';
                const cityLike = neighborhood || sublocality || locality;
                const adminArea =
                  components.find((c) =>
                    c.types.includes('administrative_area_level_1')
                  )?.short_name || '';
                const country =
                  components.find((c) => c.types.includes('country'))
                    ?.long_name || '';
                const postalCode =
                  components.find((c) => c.types.includes('postal_code'))
                    ?.long_name || '';
                const postalSuffix =
                  components.find((c) => c.types.includes('postal_code_suffix'))
                    ?.long_name || '';
                const fullPostalCode = postalSuffix
                  ? `${postalCode}-${postalSuffix}`
                  : postalCode;

                let street = [streetNumber, route].filter(Boolean).join(' ');
                if (subpremise) street += ` #${subpremise}`;

                const parts = [
                  street,
                  cityLike,
                  adminArea && fullPostalCode
                    ? `${adminArea} ${fullPostalCode}`
                    : adminArea || fullPostalCode,
                  country,
                ].filter(Boolean);

                resolve({
                  placeId: p.place_id,
                  display: parts.join(', '),
                });
              }
            )
          )
      );

      this.modalOpen = true;
      const baseSite = this.baseSiteId;
      const modalRef = this.modalService.open(AddressSuggestionsComponent, {
        centered: true,
        windowClass: baseSite,
        backdrop: 'static',
        keyboard: false,
      });
      modalRef.componentInstance.suggestions = suggestionPromises;
      modalRef.componentInstance.typed = raw;

      modalRef.result.finally(() => (this.modalOpen = false));

      const chosenPlaceId = await modalRef.result;
      if (!chosenPlaceId) return;

      const pred = predictions.find((p) => p.place_id === chosenPlaceId);
      if (!pred) return;
      this.placesService!.getDetails(
        {
          placeId: pred.place_id,
          fields: [
            'name',
            'geometry',
            'address_components',
            'formatted_address',
          ],
        },
        (place) => {
          if (place) this.addressChosen.emit(place); // Emit PlaceResult
        }
      );
    }, 300);
  }

  ngOnDestroy(): void {
    this.listener?.remove();
  }

  private async fetchPredictions(
    input: string
  ): Promise<google.maps.places.AutocompletePrediction[]> {
    return new Promise((resolve) => {
      this.autocompleteService?.getPlacePredictions(
        { input, types: ['address'], componentRestrictions: { country: 'us' } },
        (predictions, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK) {
            resolve(predictions || []);
          } else {
            resolve([]);
          }
        }
      );
    });
  }
}
